import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { TenantProvider } from "@/context/tenant-context";
import { ScoutWidget } from "@/components/assistant/scout-widget";
import { PlatformTour } from "@/components/tour/platform-tour";
import { TenantSidebar } from "./tenant-sidebar";
import { CollapsibleSidebar } from "./collapsible-sidebar";
import { MobileShell } from "./mobile-shell";
import { TenantTopBar } from "./tenant-topbar";
import {
  TenantSettingsDialog,
  type TenantSettingsDialogState,
  type TenantSettingsSection,
} from "./tenant-settings-dialog";
import { AgentSettingsDrawer } from "./agent-settings-drawer";
import { useIsMobile } from "@/hooks/use-is-mobile";

export function TenantShell() {
  const isMobile = useIsMobile();
  const [settingsDialog, setSettingsDialog] =
    useState<TenantSettingsDialogState>(null);
  // Agent settings open as a right-hand drawer, not the full settings dialog —
  // tweaking one agent shouldn't take over the whole screen with tenant nav.
  const [agentSettingsId, setAgentSettingsId] = useState<string | null>(null);

  // Let guided walkthroughs (and Scout) open the settings dialog straight at a
  // section — the dialog has no route, so this event is the only entry point.
  useEffect(() => {
    const onOpenSettings = (e: Event) => {
      const detail =
        (e as CustomEvent<{ kind?: "account" | "tenant"; section?: TenantSettingsSection }>)
          .detail ?? {};
      setSettingsDialog(
        detail.kind === "account"
          ? { kind: "account" }
          : { kind: "tenant", section: detail.section },
      );
    };
    window.addEventListener("proclaw:open-settings", onOpenSettings);
    return () => window.removeEventListener("proclaw:open-settings", onOpenSettings);
  }, []);

  return (
    <TenantProvider>
      <Toaster position="bottom-right" closeButton richColors />
      {isMobile ? (
        <MobileShell />
      ) : (
        <>
          <div className="flex h-screen w-screen overflow-hidden bg-background">
            <CollapsibleSidebar>
              <TenantSidebar
                onOpenAccountSettings={() => setSettingsDialog({ kind: "account" })}
                onOpenTenantSettings={() => setSettingsDialog({ kind: "tenant" })}
              />
            </CollapsibleSidebar>
            <main className="flex flex-1 flex-col overflow-hidden">
              <TenantTopBar
                onOpenAgentSettings={(agentId) => setAgentSettingsId(agentId ?? null)}
              />
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <Outlet />
              </div>
            </main>
          </div>
          <TenantSettingsDialog
            state={settingsDialog}
            onClose={() => setSettingsDialog(null)}
          />
          <AgentSettingsDrawer
            agentId={agentSettingsId}
            onClose={() => setAgentSettingsId(null)}
          />
          {/* Scout help assistant — fixed-position launcher, desktop only for now */}
          <ScoutWidget />
          {/* First-login walkthrough (welcome modal + spotlight steps) */}
          <PlatformTour />
        </>
      )}
    </TenantProvider>
  );
}
